import { authClient, ApiError, unwrap } from '@/shared/api-client';

// Logout can't ride the typed apiClient (Spring Security filter route, absent from the
// OpenAPI schema), so it lives in the seam. Re-exported here to keep the auth module's
// public surface stable for consumers (AccountMenu).
export { logout } from '@/shared/api-client';

export type LoginCredentials = {
  email: string;
  password: string;
};

/**
 * Thrown when login is rejected. Extends {@link ApiError} so it inherits `status`/`code`
 * while keeping its own identity: LoginPage branches on `instanceof LoginError` and
 * `status === 401` to tell bad credentials from an unexpected failure.
 */
export class LoginError extends ApiError {
  constructor(status: number, code: string | null, message: string) {
    super(status, code, message);
    this.name = 'LoginError';
  }
}

export async function login(credentials: LoginCredentials): Promise<void> {
  try {
    await unwrap(authClient.POST('/api/v1/auth/login', { body: credentials }));
  } catch (error) {
    if (error instanceof ApiError) {
      throw new LoginError(error.status, error.code, error.message);
    }
    throw error;
  }
}

export type ChangePasswordInput = {
  currentPassword: string;
  newPassword: string;
};

/**
 * Thrown when the self-service change is rejected. Extends {@link ApiError}; `code` carries
 * the typed backend reason (e.g. `invalid_current_password`) that ChangePasswordPage branches on.
 */
export class ChangePasswordError extends ApiError {
  constructor(status: number, code: string | null, message: string) {
    super(status, code, message);
    this.name = 'ChangePasswordError';
  }
}

export async function changePassword(input: ChangePasswordInput): Promise<void> {
  // A wrong current-password answer comes back as a 401; the apiClient seam exempts
  // /api/v1/auth/me/password from the global unauthorized signal so it stays a form error here
  // instead of bouncing the user to the login screen.
  try {
    await unwrap(authClient.POST('/api/v1/auth/me/password', { body: input }));
  } catch (error) {
    if (error instanceof ApiError) {
      throw new ChangePasswordError(error.status, error.code, error.message);
    }
    throw error;
  }
}
